import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { includes, isEmpty } from 'lodash';
import moment from 'moment-timezone';
import DayButton from '../day-button';
import './month.scss';

// Replaces material-ui/DatePicker/dateUtils isBetweenDates
const cloneAsDate = (date) => {
  const cloned = new Date(date.getTime());
  cloned.setHours(0, 0, 0, 0);
  return cloned;
};

const isBetweenDates = (dateToCheck, startDate, endDate) => {
  const check = cloneAsDate(dateToCheck);
  const start = cloneAsDate(startDate);
  const end = cloneAsDate(endDate);
  return check.getTime() >= start.getTime() && check.getTime() <= end.getTime();
};

const Month = ({
  DateTimeFormat,
  displayDate,
  exceptions,
  firstDayOfWeek,
  locale,
  maxDate,
  minDate,
  onTouchTapDay,
  utils,
  weekdays
}) => {
  const handleTouchTapDay = useCallback((event, date) => {
    if (onTouchTapDay) {
      onTouchTapDay(event, date);
    }
  }, [onTouchTapDay]);

  const shouldDisableDate = useCallback((day) => {
    if (!minDate || !maxDate) {
      return true;
    }
    if (day === null) {
      return false;
    }
    return !isBetweenDates(day, minDate, maxDate);
  }, [minDate, maxDate]);

  const isSelected = useCallback((day, weekDayIndex) => {
    if (!minDate || !maxDate) {
      return false;
    }
    if (day === null) {
      return false;
    }
    return isBetweenDates(day, minDate, maxDate) && includes(weekdays, weekDayIndex);
  }, [minDate, maxDate, weekdays]);

  const isExcluded = useCallback((day) => {
    if (!isEmpty(exceptions)) {
      return exceptions.some(
        exception => moment(exception.exception_date, 'YYYY-MM-DD').isSame(moment(day), 'day')
      );
    }
    return false;
  }, [exceptions]);

  const getDayElements = (week, weekIndex) => {
    return week.map((day, weekDayIndex) => {
      const disabled = shouldDisableDate(day);
      const selected = isSelected(day, weekDayIndex);
      const excluded = isExcluded(day);

      return (
        <DayButton
          DateTimeFormat={DateTimeFormat}
          excluded={excluded}
          locale={locale}
          date={day}
          disabled={disabled}
          key={`db${weekIndex + weekDayIndex}`}
          onClick={handleTouchTapDay}
          selected={selected}
        />
      );
    });
  };

  const getWeekElements = () => {
    const weekArray = utils.getWeekArray(displayDate, firstDayOfWeek);

    return weekArray.map((week, weekIndex) => (
      <div key={weekIndex} styleName="week">
        {getDayElements(week, weekIndex)}
      </div>
    ));
  };

  return (
    <div styleName="custom-calendar-month-container">
      {getWeekElements()}
    </div>
  );
};

Month.propTypes = {
  DateTimeFormat: PropTypes.func.isRequired,
  displayDate: PropTypes.object.isRequired,
  exceptions: PropTypes.array,
  firstDayOfWeek: PropTypes.number,
  locale: PropTypes.string.isRequired,
  maxDate: PropTypes.object,
  minDate: PropTypes.object,
  onTouchTapDay: PropTypes.func,
  utils: PropTypes.object.isRequired,
  weekdays: PropTypes.array
};

export default Month;
